import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InputMask from "react-input-mask";
import { Alert, Button, Card, Col, Form, Input, Row, Select, Table, Tabs, Tag, Typography, message } from "antd";
import { EnvironmentOutlined, ExportOutlined, RightOutlined } from "@ant-design/icons";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { withPatientReferenceParams } from "../../services/zoomSchedulerLinks";
import { adminPatientQuickSignUp, getAdminDoctorsForPatient } from "../redux/adminSlice";
import neutralDoctorAvatar from "../../assets/images/users/clinician_default_avatar.png";
import "../QuickStart/QuickSelfBooking.css";
import "./Admin.css";

const { Text, Title } = Typography;

const phoneRegex = /^\+1-\d{3}-\d{3}-\d{4}$/;
const normalizePhone = (value) =>
  String(value || "")
    .replace(/[‐‑‒–—−]/g, "-")
    .replace(/\s+/g, "")
    .trim();

const sexOptions = ["Female", "Male", "Intersex", "Prefer not to say"].map((v) => ({ label: v, value: v }));
const signupLocationOptions = ["Calgary", "Edmonton"].map((v) => ({ label: v, value: v }));
const filterLocationOptions = [{ label: "All locations", value: "" }, ...signupLocationOptions];

const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const toPatient = (p) => ({
  userRef: p.userRef || "",
  firstName: p.firstname || "",
  lastName: p.lastname || "",
  email: p.email || "",
});


async function fetchAllPatients(token) {
  const rows = await fetchAllPages("Admin/GetPatientList", token);
  return rows.map(toPatient);
}

const errorText = (err, fallback) =>
  (Array.isArray(err?.errors) && err.errors.join(", ")) || err?.message || fallback;

const mapDoctor = (d) => {
  const doctorId = d?.userId ?? d?.UserId ?? d?.doctorId ?? d?.DoctorId ?? d?.id ?? d?.Id;
  const name = d?.doctorName ?? d?.DoctorName ?? d?.name ?? d?.Name;
  if (!doctorId || !name) return null;
  const slots = d?.slots ?? d?.Slots ?? [];
  const locations = Array.from(
    new Set(
      (Array.isArray(slots) ? slots : []).map((s) => toTitleCase(s?.location ?? s?.Location)).filter(Boolean),
    ),
  );
  return {
    id: `doctor-${doctorId}`,
    name: String(name),
    title: d?.roleName ?? d?.RoleName ?? "",
    bio: d?.bio ?? d?.Bio ?? "",
    photo: d?.picture || d?.Picture || neutralDoctorAvatar,
    locations,
    initialBookingLink: d?.initialBookingLink ?? d?.InitialBookingLink ?? "",
    followUpBookingLink: d?.followUpBookingLink ?? d?.FollowUpBookingLink ?? "",
    hasSufficientPoints: (d?.hasSufficientPoints ?? d?.HasSufficientPoints) !== false,
  };
};

const BookForPatient = () => {
  const dispatch = useDispatch();
  const routeLocation = useLocation();
  const userAuth = useSelector((state) => state?.authentication?.userAuth);
  const token = userAuth?.obj?.token;

  const preselected = routeLocation.state?.patient || null;

  const [step, setStep] = useState(preselected ? "provider" : "patient");
  const [patient, setPatient] = useState(preselected);
  const [patientTab, setPatientTab] = useState("existing");

  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [search, setSearch] = useState({ account: "", firstName: "", lastName: "", email: "" });

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const [location, setLocation] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [clinician, setClinician] = useState(null);
  const [openedScheduler, setOpenedScheduler] = useState(false);

  const loadPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const list = await fetchAllPatients(token);
      setPatients(list);
      return list;
    } catch (error) {
      message.error("Could not load patients. Please try again.");
      return [];
    } finally {
      setPatientsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const filteredPatients = useMemo(() => {
    const has = (value, term) => String(value || "").toLowerCase().includes(term.trim().toLowerCase());
    return patients.filter(
      (p) =>
        has(p.userRef, search.account) &&
        has(p.firstName, search.firstName) &&
        has(p.lastName, search.lastName) &&
        has(p.email, search.email),
    );
  }, [patients, search]);

  const patientColumns = [
    { title: "Account #", dataIndex: "userRef", sorter: (a, b) => a.userRef.localeCompare(b.userRef) },
    { title: "First Name", dataIndex: "firstName", sorter: (a, b) => a.firstName.localeCompare(b.firstName) },
    { title: "Last Name", dataIndex: "lastName", sorter: (a, b) => a.lastName.localeCompare(b.lastName) },
    { title: "Email", dataIndex: "email" },
    {
      title: "",
      key: "select",
      render: (_, record) => (
        <Button type="primary" size="small" style={{ background: "#00ADEF" }} onClick={() => handleSelectPatient(record)}>
          Select
        </Button>
      ),
    },
  ];

  const handleSelectPatient = (p, defaultLocation = "") => {
    setPatient(p);
    setLocation(defaultLocation);
    setClinician(null);
    setOpenedScheduler(false);
    setStep("provider");
  };

  const handleSignUp = async (values) => {
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phoneNumber: normalizePhone(values.phone),
      gender: values.gender,
      refSource: values.refSource.trim(),
      location: values.location,
    };

    setSubmitting(true);
    try {
      const result = await dispatch(adminPatientQuickSignUp(payload));
      if (adminPatientQuickSignUp.rejected.match(result)) {
        message.error(errorText(result.payload, "Patient sign up failed. Please try again."));
        return;
      }

      message.success(result.payload?.message || "Patient signed up.");
      const list = await loadPatients();
      const created = list.find((p) => p.email.toLowerCase() === payload.email.toLowerCase());
      if (!created) {
        message.error("Patient was created but could not be found in the list. Pick them from the Existing patient tab.");
        setPatientTab("existing");
        return;
      }
      form.resetFields();
      handleSelectPatient({ ...created, id: result.payload?.patientId }, payload.location);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step !== "provider" || !patient?.userRef) return undefined;
    let cancelled = false;

    const run = async () => {
      setDoctorsLoading(true);
      const result = await dispatch(getAdminDoctorsForPatient({ userRef: patient.userRef, location }));
      if (cancelled) return;
      if (getAdminDoctorsForPatient.fulfilled.match(result)) {
        const raw = result.payload?.data ?? result.payload;
        setDoctors((Array.isArray(raw) ? raw : []).map(mapDoctor).filter((d) => d && (d.initialBookingLink || d.followUpBookingLink)));
      } else {
        setDoctors([]);
        message.error(errorText(result.payload, "Could not load providers. Please try again."));
      }
      setDoctorsLoading(false);
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [dispatch, step, patient?.userRef, location]);

  const schedulerOptions = useMemo(
    () =>
      [
        { key: "initial", label: "Open initial scheduler", link: clinician?.initialBookingLink },
        { key: "followUp", label: "Open follow-up scheduler", link: clinician?.followUpBookingLink },
      ]
        .map((o) => ({ ...o, url: withPatientReferenceParams(o.link, patient) }))
        .filter((o) => !!o.url),
    [clinician, patient],
  );

  const handleOpenScheduler = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpenedScheduler(true);
  };

  const handleReset = () => {
    setPatient(null);
    setClinician(null);
    setDoctors([]);
    setOpenedScheduler(false);
    setLocation("");
    setStep("patient");
  };

  const patientSummary = patient && (
    <Text type="secondary">
      Booking for{" "}
      <Text strong>
        {patient.firstName} {patient.lastName}
      </Text>{" "}
      ({patient.email})
    </Text>
  );

  return (
    <div style={{ margin: "2rem" }}>
      <p style={{ color: "#335CAD", fontWeight: "bold", fontSize: 16 }}>Book for Patient</p>

      {step === "patient" && (
        <Card>
          <Tabs
            activeKey={patientTab}
            onChange={setPatientTab}
            items={[
              {
                key: "existing",
                label: "Existing patient",
                children: (
                  <>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                      {[
                        ["account", "Account #"],
                        ["firstName", "First Name"],
                        ["lastName", "Last Name"],
                        ["email", "Email"],
                      ].map(([key, label]) => (
                        <Col xs={24} sm={12} md={6} key={key}>
                          <Input
                            allowClear
                            placeholder={label}
                            value={search[key]}
                            onChange={(e) => setSearch((prev) => ({ ...prev, [key]: e.target.value }))}
                          />
                        </Col>
                      ))}
                    </Row>
                    <Table
                      rowKey="userRef"
                      size="small"
                      loading={patientsLoading}
                      columns={patientColumns}
                      dataSource={filteredPatients}
                      pagination={{ pageSize: 10, showSizeChanger: false }}
                      scroll={{ x: true }}
                    />
                  </>
                ),
              },
              {
                key: "new",
                label: "New patient",
                children: (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSignUp}
                    initialValues={{ gender: "Female" }}
                    style={{ maxWidth: 720 }}
                  >
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="firstName"
                          label="First Name"
                          rules={[
                            { required: true, message: "Please enter a first name" },
                            { whitespace: true, message: "First name cannot be empty" },
                          ]}
                        >
                          <Input placeholder="Jane" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="lastName"
                          label="Last Name"
                          rules={[
                            { required: true, message: "Please enter a last name" },
                            { whitespace: true, message: "Last name cannot be empty" },
                          ]}
                        >
                          <Input placeholder="Doe" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="gender"
                          label="Sex at Birth"
                          rules={[{ required: true, message: "Please select sex at birth" }]}
                        >
                          <Select options={sexOptions} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="phone"
                          label="Phone"
                          validateTrigger={["onChange", "onBlur"]}
                          getValueFromEvent={(e) => e?.target?.value}
                          normalize={(v) => normalizePhone(v)}
                          rules={[
                            { required: true, message: "Please enter a phone number" },
                            {
                              validator: (_, value) =>
                                !value || phoneRegex.test(normalizePhone(value))
                                  ? Promise.resolve()
                                  : Promise.reject(new Error("Use +1-XXX-XXX-XXXX format (e.g. +1-416-555-1234)")),
                            },
                          ]}
                        >
                          <InputMask mask="+1-999-999-9999" maskChar={null}>
                            {(inputProps) => <Input {...inputProps} placeholder="+1-416-555-1234" />}
                          </InputMask>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="email"
                          label="Email"
                          rules={[
                            { required: true, message: "Please enter an email" },
                            { type: "email", message: "Please enter a valid email address" },
                          ]}
                        >
                          <Input placeholder="jane@example.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="refSource"
                          label="How did they hear about us?"
                          rules={[
                            { required: true, message: "Please enter how they heard about us" },
                            { whitespace: true, message: "This field cannot be empty" },
                          ]}
                        >
                          <Input placeholder="Phone call, Google, Friend, etc." />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="location"
                          label="Location"
                          rules={[{ required: true, message: "Please select a location" }]}
                        >
                          <Select placeholder="Select a location" options={signupLocationOptions} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      style={{ background: "#00ADEF", borderColor: "#00ADEF" }}
                    >
                      Sign up patient and continue
                    </Button>
                  </Form>
                ),
              },
            ]}
          />
        </Card>
      )}

      {step === "provider" && patient && (
        <Card>
          <Button type="link" onClick={handleReset} style={{ paddingLeft: 0 }}>
            ← Change patient
          </Button>
          <Title level={5} style={{ marginTop: 0 }}>
            Choose a provider
          </Title>
          <div style={{ marginBottom: 16 }}>{patientSummary}</div>
          <Select
            value={location}
            onChange={setLocation}
            options={filterLocationOptions}
            style={{ width: 200, marginBottom: 16 }}
          />

          {doctorsLoading && <Text type="secondary">Loading providers…</Text>}
          {!doctorsLoading &&
            doctors.map((d) => (
              <button
                key={d.id}
                type="button"
                className="quick-book-clinician-row"
                onClick={() => {
                  setClinician(d);
                  setOpenedScheduler(false);
                  setStep("schedule");
                }}
              >
                <img
                  className="quick-book-clinician-avatar"
                  src={d.photo}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = neutralDoctorAvatar;
                  }}
                />
                <div className="quick-book-clinician-body">
                  <h3 className="quick-book-clinician-name">
                    {d.name}
                    {d.title ? ` (${d.title})` : ""}
                    {!d.hasSufficientPoints && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        Patient has insufficient credits
                      </Tag>
                    )}
                  </h3>
                  {!!d.bio && <p className="quick-book-clinician-bio">{d.bio}</p>}
                  {!!d.locations.length && (
                    <div className="quick-book-clinician-location">
                      <EnvironmentOutlined className="quick-book-location-icon" aria-hidden />
                      <span className="quick-book-location-text">{d.locations.join(", ")}</span>
                    </div>
                  )}
                </div>
                <RightOutlined className="quick-book-chevron" />
              </button>
            ))}
          {!doctorsLoading && doctors.length === 0 && (
            <Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              No providers with a scheduling link found{location ? ` for ${location}` : ""}.
            </Text>
          )}
        </Card>
      )}

      {step === "schedule" && patient && clinician && (
        <Card style={{ maxWidth: 640 }}>
          <Button type="link" onClick={() => setStep("provider")} style={{ paddingLeft: 0 }}>
            ← Back to providers
          </Button>
          <Title level={5} style={{ marginTop: 0 }}>
            Book with {clinician.name}
            {clinician.title ? ` (${clinician.title})` : ""}
          </Title>
          <div style={{ marginBottom: 16 }}>{patientSummary}</div>

          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Keep the patient's registered email on the Zoom form"
            description={
              <>
                The booking is matched to the patient by email. Leave <Text strong>{patient.email}</Text> as
                entered on Zoom&apos;s page - do not replace it with your own.
              </>
            }
          />
          {!clinician.hasSufficientPoints && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="This patient has insufficient credits for this provider (for information only)."
            />
          )}
          {openedScheduler && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Finish booking in the new tab"
              description="Once the time is submitted on Zoom's page the booking is created automatically and the patient gets a confirmation email."
            />
          )}

          <div style={{ display: "flex", gap: 12 }}>
            {schedulerOptions.map((o) => (
              <Button
                key={o.key}
                type="primary"
                size="large"
                icon={<ExportOutlined />}
                style={{ flex: 1, background: "#00ADEF", borderColor: "#00ADEF" }}
                onClick={() => handleOpenScheduler(o.url)}
              >
                {o.label}
              </Button>
            ))}
          </div>
          {openedScheduler && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button type="link" onClick={handleReset} style={{ color: "#64748b" }}>
                Book another patient
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default BookForPatient;
